export const ALLOWED_REDIRECT_DOMAINS: readonly string[] = [
  'autoavaliar.com.br',
  'auto-action.com',
];

export const PASSWORD_MIN_LENGTH = 6;

export const PASSWORD_MAX_LENGTH = 30;

export const REMEMBERED_LOGIN_KEY = 'rememberedLogin';

export const LEGACY_LOGIN_DATA_KEY = 'loginData';

export const LOGIN_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  INVALID_CREDENTIALS: 'LOGIN.ERRORS.INVALID_CREDENTIALS',
  USER_WITHOUT_PERMISSIONS: 'VEHICLE_ACTIONS.USER_WITHOUT_PERMISSION',
};

export const GENERIC_ERROR_MESSAGE_KEY = 'GENERIC.ERRORS.GENERIC';

export const LOGIN_LOGO_PATH = 'assets/img/logos/Logo_Default.png';

export type CountryCode = 'BR' | 'PT' | 'ES';

export type DeployedEnvironment = 'prod' | 'homolog';

export type LoginEnvironment = DeployedEnvironment | 'local' | 'unknown';

export interface CountryOption {
  code: CountryCode;
  labelKey: string;
}

const COUNTRY_HOSTS: Readonly<Record<CountryCode, Record<DeployedEnvironment, string>>> = {
  BR: {
    prod: 'sync.autoavaliar.com.br',
    homolog: 'sync-homolog.autoavaliar.com.br',
  },
  PT: {
    prod: 'sync-pt.auto-action.com',
    homolog: 'sync-pt-homolog.auto-action.com',
  },
  ES: {
    prod: 'sync-es.auto-action.com',
    homolog: 'sync-es-homolog.auto-action.com',
  },
};

export const COUNTRY_OPTIONS: readonly CountryOption[] = (
  Object.keys(COUNTRY_HOSTS) as CountryCode[]
).map((code) => ({ code, labelKey: `LOGIN.COUNTRIES.${code}` }));

const LOCAL_ADDRESS =
  /^(\[?::1\]?|127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.|0\.0\.0\.0$)/;

const DEPLOYED_HOSTS: ReadonlyMap<string, DeployedEnvironment> = new Map(
  (Object.keys(COUNTRY_HOSTS) as CountryCode[]).flatMap((code) =>
    (['prod', 'homolog'] as DeployedEnvironment[]).map(
      (environment) => [COUNTRY_HOSTS[code][environment], environment] as const
    )
  )
);

export function detectEnvironment(hostname: string): LoginEnvironment {
  const host = hostname.toLowerCase();

  if (host.includes('localhost') || LOCAL_ADDRESS.test(host)) {
    return 'local';
  }

  return DEPLOYED_HOSTS.get(host) ?? 'unknown';
}

export function detectCountryCode(hostname: string): CountryCode {
  const host = hostname.toLowerCase();
  const match = COUNTRY_OPTIONS.find((country) =>
    host.includes(`sync-${country.code.toLowerCase()}`)
  );
  return match ? match.code : 'BR';
}

function countryHostPrefix(code: CountryCode): string {
  return code === 'BR' ? 'sync' : `sync-${code.toLowerCase()}`;
}

function localCountryHost(hostname: string, code: CountryCode): string | null {
  const host = hostname.toLowerCase();

  if (!host.endsWith('localhost')) {
    return null;
  }

  const labels = host.split('.');

  if (!labels[0].startsWith('sync') && code === detectCountryCode(host)) {
    return host;
  }

  return `${countryHostPrefix(code)}.${labels[labels.length - 1]}`;
}

export function resolveCountryUrl(hostname: string, code: string, port = ''): string | null {
  const country = COUNTRY_OPTIONS.find((option) => option.code === code);
  const environment = detectEnvironment(hostname);

  if (!country || environment === 'unknown') {
    return null;
  }

  const host =
    environment === 'local'
      ? localCountryHost(hostname, country.code)
      : COUNTRY_HOSTS[country.code][environment];

  return host ? `https://${host}${port ? `:${port}` : ''}` : null;
}

export function isSafeCountryUrl(url: string, hostname: string): boolean {
  if (detectEnvironment(hostname) !== 'local') {
    return isAllowedRedirect(url);
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname.toLowerCase().endsWith('localhost');
  } catch {
    return false;
  }
}

export function isAllowedRedirect(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') {
    return false;
  }

  const host = parsed.hostname.toLowerCase();
  return ALLOWED_REDIRECT_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

export function extractLegacyLogin(blob: string): string | null {
  try {
    const parsed = JSON.parse(atob(blob)) as { userloginRaw?: unknown };
    const login = parsed?.userloginRaw;
    return typeof login === 'string' && login.length > 0 ? login : null;
  } catch {
    return null;
  }
}
